'use strict';


import { DynamicDrawUsage, Euler, Group, InstancedMesh, Matrix4, Quaternion, Vector3 } from 'three';
import { SelectedObjectType } from '../../common';
import {Buildings, projectMode, settingsData} from '../../settings';
import { createBuildingInfo } from '../threejs/autoSelectionBoxes';

export class Building {
    constructor (model) {
        this.model = model;
        this.modelGroup = new Group();
        this.selectionsGroup = null;
        this.name = this.model.name;
        this.apartments = [];
        this.floors = [];
        this.amenities = {};
        this.instances = [];
        this.selections = [];
        this.floorsPlug = [];
        this.customSelections = [];
        this.info = {};
        this.isActive = false;
        this.isHide = false;
        this.mouseHover = false;
        this.floorSliderNumbers = [];
    }

    create() {
        /* Sort floors by Y */
        this.model.children = this.model.children.sort((prev, next) => prev.position.y - next.position.y);

        /* Create main Group */
        this.modelGroup.name = this.model.name;
        this.modelGroup.position.set(this.model.position.x, this.model.position.y, this.model.position.z);
        this.modelGroup.scale.set(this.model.scale.x, this.model.scale.y, this.model.scale.z);
        this.modelGroup.rotation.set(this.model.rotation.x, this.model.rotation.y, this.model.rotation.z);

        let instanceMesh;

        /* Create floor instance Mesh */
        this.model.children.forEach(floor => {
            if (floor.name.match(/floor/g)) {
                instanceMesh = makeInstanceMesh(this,floor, undefined, 'floor');
                this.instances.push(instanceMesh);
                this.modelGroup.add(instanceMesh);
            } else {
                instanceMesh = makeInstanceMesh(this,floor, 1, 'amenity');
                this.instances.push(instanceMesh);
                this.modelGroup.add(instanceMesh);
            }
        });

        if (projectMode.LiteMode.isActive() && !projectMode.LiteMode.modeIs('regular')) this.modelGroup = null;

        /* Get numbers for floor slider */
        if (projectMode.LiteMode.isActive() && !projectMode.LiteMode.modeIs('regular')) {
            for (let i = 0; i < this.model.children.length; i++) {
                this.floorSliderNumbers.push(this.model.children.length - i);
            }
        } else {
            this.floorSliderNumbers = this.floors.map(el => el.floor).reverse();
        }
        /* Add model to intersection */
        settingsData.objects_to_intersection.push(...this.instances);
        settingsData.instanced_floors_switch[this.name] = this.instances;

        /* Add building to District */
        Buildings.addBuilding(this.name,this);

        return this.modelGroup;
    }

    addFloor(value){
        this.floors.push(value);
    }

    addAmenities(key,value) {
        if (projectMode.LiteMode.isActive() && !projectMode.LiteMode.modeIs('regular')) return;
        this.amenities[key] = value;
    }

    addSelection(data){
        this.selections.push(data);
    }

    addInfo(value){
        this.info = value;
    }

    getInfo(){
        if (Object.keys(this.info).length){
            return this.info;
        } else {
            return false;
        }
    }

    getFloorPlugMeshes(){
        return this.floorsPlug.map(plug => plug.data);
    }

    getFloorsMoreThan(number) {
        if (projectMode.LiteMode.isActive() && !projectMode.LiteMode.modeIs('regular'))
            return [];
        return this.floors.filter(obj => obj.floor > number).map(el => el.data);
    }

    getFloorSelections(number, withPlug = true){
        if (withPlug){
            return this.selections.find(selection => selection.floor === number).data;
        } else {
            return this.selections.find(selection => selection.floor === number).data
                .filter(apt => !apt.name.startsWith(config.resources.selectionBoxes.floorPlugName));
        }
    }

    getFloorsSelectionsGroupMoreThan(number) {
        return this.selections.filter(selection => selection.floor > number).reduce((acc,value) => {
            acc.push(value.data[0].parent);
            return acc;
        },[]);
    }

    getFloors(start,end) {
        let floors = [];
        let floorSelectionGroup = [];

        if (!end) {
            floors = this.getFloorsMoreThan(start);
            floorSelectionGroup = this.getFloorsSelectionsGroupMoreThan(start);
        } else {
            floors = this.floors.filter(obj => obj.floor > start && obj.floor <= end).map(el => el.data);
            floorSelectionGroup = this.selections.filter(selection => selection.floor > start && selection.floor <= end).reduce((acc,value) => {
                acc.push(value.data[0].parent);
                return acc;
            },[]);
        }

        return {
            floors,
            floorSelectionGroup
        }
    }



    getFloorNumbersForFloorSlider(){
        return this.floorSliderNumbers;
    }

    activate(){
        this.isActive = true;
    }

    deactivate(){
        this.isActive = false;
    }

}

export class BuildingWithoutModel extends Building{

    constructor (model,props,settings) {
        super(model);

        this.name = model;
        this.info = createBuildingInfo({name: this.model},props,settings);

        this.info.crm_data.forEach(el => {

            if (el.facing){
                const  facing = el.facing.split(/,|-/);
                el.facing = [...new Set(facing)];
            } else {
                el.facing = [];
            }

        });


        return this;
    }
}


function makeInstanceMesh(scope,baseMesh, count, type) {
    let rotation = new Euler(baseMesh.rotation.x, baseMesh.rotation.y, baseMesh.rotation.z, 'XYZ');
    let rotationQ = new Quaternion().setFromEuler(rotation);
    let matrix = new Matrix4();
    let scale = new Vector3(baseMesh.scale.x, baseMesh.scale.y, baseMesh.scale.z);
    let position;
    let floorHeight = 0;
    let from, to = 0;
    let iteration = (count) ? count : 0;
    let isFloor = false;
    let key;
    let value;
    let floorFrom;
    let floorTo;

    if (type === 'floor') {
        let [buildingName, floorName, modelFloorHeight] = baseMesh.name.split('_');
        const floorHeightInMeter = parseFloat(modelFloorHeight) / 100;
        floorName = floorName.replace('floor','');

        /* Checking floor TO value */
        if (floorName.match(/((-\d{1,})|(\d{1,})).((-\d{1,})|(\d{1,}))/g)){
            const split = floorName.split('-');
            floorFrom = split[0];
            floorTo = split[1];

        } else {
            floorFrom = floorName;
            floorTo = floorName;
        }

        from = Number(floorFrom);
        to = Number(floorTo);
        floorHeight = floorHeightInMeter;
        iteration =  to - from;

        if (iteration === 0){
            iteration = 1;
        } else {
            iteration++;
        }
        isFloor = true;
    }

    let elementBuildingInstance = new InstancedMesh(baseMesh.geometry, baseMesh.material, iteration);
    elementBuildingInstance.instanceMatrix.setUsage(DynamicDrawUsage);

    for (let i = 0; i < iteration; i++) {
        const floorPosY = baseMesh.position.y + i * floorHeight;
        position = new Vector3(baseMesh.position.x, floorPosY, baseMesh.position.z);
        matrix.compose(position, rotationQ, scale);
        elementBuildingInstance.setMatrixAt(i, matrix);
        elementBuildingInstance.renderOrder = 1;

        switch (type) {

            case 'floor':
                key = from + i;
                value = {
                    'name': baseMesh.name,
                    'mesh': elementBuildingInstance,
                    'count': i,
                    'position': {x: position.x, y: position.y, z: position.z},
                    'basePosition': {x: position.x, y: position.y, z: position.z},
                    'rotation': rotation,
                    'diff': {
                        x: 0,
                        y: position.y,
                        z: 0,
                    },
                    'matrix': matrix
                };

                const floorObj = {floor: key, data: value};

                scope.addFloor(floorObj);

                break;
            case 'amenity':
                key = baseMesh.name;
                key = key.replace(/.\d{1,}/g,'');
                value = {
                    'mesh': elementBuildingInstance
                };

                scope.addAmenities(key,value);

                break;
        }
    }

    elementBuildingInstance.name = `${baseMesh.name}_instance`;
    elementBuildingInstance.userData = {
        type: SelectedObjectType.Building,
        main_building_item: true,
        buildingFloors: true,
    };

    elementBuildingInstance.frustumCulled = false;
    elementBuildingInstance.receiveShadow = true;
    elementBuildingInstance.castShadow = true;

    return elementBuildingInstance;
}